/**
 * Nach einem Deploy gibt es die gehashten Bündel des alten Stands nicht mehr: ein offener Tab scheitert beim
 * Nachladen einer Seite („Failed to fetch dynamically imported module“). Einmaliges Neuladen holt den neuen Stand.
 * Ein Zeitstempel in `sessionStorage` verhindert eine Schleife, falls der Fehler eine andere Ursache hat.
 */

export const RELOAD_GUARD_KEY = 'socialfy.chunkReloadAt';
export const RELOAD_GUARD_MS = 30_000;

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

/** Ladefehler eines Seitenbündels (Chromium/Firefox: „Failed to fetch …“, Safari: „Importing a module script failed“). */
export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
}

/**
 * Lädt die Seite neu, außer es gab innerhalb des Schutzfensters schon einen automatischen Reload. Ist
 * `sessionStorage` nicht nutzbar, lässt sich eine Schleife nicht ausschließen: dann kein Reload.
 * @returns ob neu geladen wurde
 */
export function reloadOnce(
  reload: () => void = () => window.location.reload(),
  now: number = Date.now(),
): boolean {
  try {
    const storage = window.sessionStorage;
    const last = Number(storage.getItem(RELOAD_GUARD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD_GUARD_MS) {
      return false;
    }
    storage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Hört auf das Vite-Ereignis `vite:preloadError`; bei einem Reload wird der Fehler unterdrückt, sonst läuft er weiter. */
export function installChunkReload(target: EventTarget = window, reload?: () => void): void {
  target.addEventListener('vite:preloadError', (event) => {
    if (reloadOnce(reload)) {
      event.preventDefault();
    }
  });
}
